import {
  AddRegular,
  ArrowDown20Regular,
  ArrowUp20Regular,
  DeleteRegular,
  EditRegular,
} from "@fluentui/react-icons";
import {
  adminDeleteProjectIqCapability,
  adminGetProjectIqCapabilities,
  swapCapabilityOrder,
} from "apps/project-iq/api";
import { ICapability } from "apps/project-iq/models/ICapability";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ITableColumn } from "models/ITableColumn";
import { useEffect, useMemo, useState } from "react";
import { EditCapabilityDialog } from "./EditCapabilityDialog";
import { useManageIqCapabilitiesStyles } from "./ManageIqCapabilities.styles";
import { IDialogState } from "./ManageIqCapabilities.types";

export const ManageIqCapabilities = (): JSX.Element => {
  const colStyles = useAdminColumnStyles();
  const capStyles = useManageIqCapabilitiesStyles();
  const [capabilities, setCapabilities] = useState<ICapability[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<IDialogState>({
    open: false,
    capability: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<ICapability | null>(null);
  const [isReordering, setIsReordering] = useState<boolean>(false);

  const loadCapabilities = async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await adminGetProjectIqCapabilities();
      setCapabilities(data as ICapability[]);
    } catch {
      setError("Failed to load capabilities. Please try again.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void loadCapabilities();
  }, []);

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await adminDeleteProjectIqCapability(deleteTarget.id);
      setDeleteTarget(null);
      void loadCapabilities();
    } catch {
      setError("Failed to delete capability. Please try again.");
      setDeleteTarget(null);
    }
  };

  const handleSave = (): void => {
    setDialogState({ open: false, capability: null });
    void loadCapabilities();
  };

  const handleDismiss = (): void => {
    setDialogState({ open: false, capability: null });
  };

  const swap = async (a: ICapability, b: ICapability): Promise<void> => {
    await swapCapabilityOrder(a.id, b.id);
  };

  const handleMoveUp = async (item: ICapability): Promise<void> => {
    const index = sortedItems.findIndex((c) => c.id === item.id);
    if (index <= 0) return;
    const neighbor = sortedItems[index - 1];
    setIsReordering(true);
    try {
      await swap(item, neighbor);
      setCapabilities((prev) =>
        prev.map((c) => {
          if (c.id === item.id) return { ...c, order: neighbor.order };
          if (c.id === neighbor.id) return { ...c, order: item.order };
          return c;
        }),
      );
    } catch {
      setError("Failed to reorder capabilities.");
    } finally {
      setIsReordering(false);
    }
  };

  const handleMoveDown = async (item: ICapability): Promise<void> => {
    const index = sortedItems.findIndex((c) => c.id === item.id);
    if (index >= sortedItems.length - 1) return;
    const neighbor = sortedItems[index + 1];
    setIsReordering(true);
    try {
      await swap(item, neighbor);
      setCapabilities((prev) =>
        prev.map((c) => {
          if (c.id === item.id) return { ...c, order: neighbor.order };
          if (c.id === neighbor.id) return { ...c, order: item.order };
          return c;
        }),
      );
    } catch {
      setError("Failed to reorder capabilities.");
    } finally {
      setIsReordering(false);
    }
  };

  const searchFields = useMemo(() => ["title"] as (keyof ICapability)[], []);

  const { searchQuery, handleSearchChange, filteredItems } =
    useAdminListSearch<ICapability>({
      items: capabilities,
      searchFields,
    });

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<ICapability>({
    cacheKey: "IqCapabilities",
    defaultSortColumn: "order",
    defaultDescending: false,
  });

  const columns: ITableColumn<ICapability>[] = useMemo(
    () => [
      {
        columnId: "icon",
        displayValue: "Icon",
        className: colStyles.normal,
        renderHeaderCell: () => "Icon",
        compare: (a: ICapability, b: ICapability) =>
          a.icon.localeCompare(b.icon),
        renderCell: (item: ICapability) => (
          <span className={capStyles.iconCell}>{item.icon}</span>
        ),
      },
      {
        columnId: "title",
        displayValue: "Title",
        className: colStyles.wide,
        renderHeaderCell: () => "Title",
        compare: (a: ICapability, b: ICapability) =>
          a.title.localeCompare(b.title),
        renderCell: (item: ICapability) => (
          <span className={capStyles.titleCell}>{item.title}</span>
        ),
      },
      {
        columnId: "order",
        displayValue: "Order",
        className: colStyles.normal,
        renderHeaderCell: () => "Order",
        compare: (a: ICapability, b: ICapability) => a.order - b.order,
        renderCell: (item: ICapability) => <span>{item.order}</span>,
      },
      {
        columnId: "enabled",
        displayValue: "Enabled",
        className: colStyles.normal,
        renderHeaderCell: () => "Enabled",
        compare: (a: ICapability, b: ICapability) =>
          Number(a.enabled) - Number(b.enabled),
        renderCell: (item: ICapability) => (
          <span
            className={
              item.enabled ? capStyles.cellEnabled : capStyles.cellDisabled
            }
          >
            {item.enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
    ],
    [colStyles, capStyles],
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns),
    [filteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<ICapability>({
    items: sortedItems,
    itemsPerPage: 15,
  });

  const rowActions: IAdminRowAction<ICapability>[] = useMemo(
    () => [
      {
        icon: <ArrowUp20Regular />,
        tooltip: "Move Up",
        onClick: (item: ICapability) => void handleMoveUp(item),
        disabled: (item: ICapability) =>
          isReordering || sortedItems.findIndex((c) => c.id === item.id) <= 0,
      },
      {
        icon: <ArrowDown20Regular />,
        tooltip: "Move Down",
        onClick: (item: ICapability) => void handleMoveDown(item),
        disabled: (item: ICapability) =>
          isReordering ||
          sortedItems.findIndex((c) => c.id === item.id) >=
            sortedItems.length - 1,
      },
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item: ICapability) =>
          setDialogState({ open: true, capability: item }),
        disabled: () => isReordering,
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item: ICapability) => setDeleteTarget(item),
        disabled: () => isReordering,
      },
    ],
    [sortedItems, isReordering],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Add New",
        icon: <AddRegular />,
        appearance: "primary",
        onClick: () => setDialogState({ open: true, capability: null }),
      },
    ],
    [],
  );

  return (
    <>
      <AdminListPage<ICapability>
        title="Capabilities"
        items={capabilities}
        columns={columns}
        isLoading={loading}
        headerActions={headerActions}
        searchPlaceholder="Search capabilities..."
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        rowActions={rowActions}
        getRowId={(item) => item.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={
          error ??
          (searchQuery
            ? "No capabilities match the search."
            : "No capabilities found.")
        }
        actionsWidth="160px"
      />
      <EditCapabilityDialog
        open={dialogState.open}
        capability={dialogState.capability}
        existingTitles={capabilities
          .filter((c) => c.id !== dialogState.capability?.id)
          .map((c) => c.title)}
        onSave={handleSave}
        onDismiss={handleDismiss}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title="Delete Capability"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeleteTarget(null)}
      />
    </>
  );
};
